import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { snackBarConfig } from 'src/app/app.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class TokenInterceptor implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private translateService: TranslateService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): any {
    if (this.authService.getToken() && req.url.indexOf('/oauth/token') === -1) {
      req = this.addTokenToRequest(req, this.authService.getToken());
    }

    return next.handle(req).pipe(
      catchError(error => {
        if (error.status === 401) {
          return this.handle401Error(req, next);
        }
        const isTokenRefresh =
          req.url.indexOf('/oauth/token') !== -1 && this.isRefreshingToken;
        const isImageRequest = req.url.indexOf('/profile-images/') !== -1;
        if (!isTokenRefresh && !isImageRequest) {
          this.handleError(error);
        }
        return throwError(error);
      })
    ) as any;
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any> | any> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(null);
      if (!this.authService.isTokenExpired('refresh_token')) {
        return this.authService.refreshToken().pipe(
          switchMap(res => {
            this.isRefreshingToken = false;
            this.authService.setSession(res.access_token, res.refresh_token);
            this.tokenSubject.next(this.authService.getToken());
            return next.handle(
              this.addTokenToRequest(request, this.authService.getToken())
            );
          }),
          catchError(() => {
            this.isRefreshingToken = false;
            const msg = this.translateService.instant('error.sessionInvalidated');
            this.snackBar.open(msg, 'X', { ...snackBarConfig, duration: 5000 });
            this.authService.logout();
            return EMPTY;
          })
        );
      } else {
        this.isRefreshingToken = false;
        this.authService.logout();
      }
    } else {
      return this.tokenSubject.asObservable().pipe(
        filter(val => val != null),
        take(1),
        switchMap(() => {
          this.isRefreshingToken = false;
          return next.handle(
            this.addTokenToRequest(request, this.authService.getToken())
          );
        })
      );
    }
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): any {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private handleError(errorReponse: HttpErrorResponse) {
    if (errorReponse.error instanceof Blob) {
      errorReponse.error.text().then(text => {
        let description: string | null;
        try {
          description = JSON.parse(text).error_description;
        } catch {
          description = null;
        }
        this.translateService.get('error.' + errorReponse.status).subscribe(statusCode => {
          this.snackBar.open(description ?? statusCode, description ? statusCode : null, snackBarConfig);
        });
      });
      return;
    }
    this.translateService.get('error.' + errorReponse.status).subscribe((statusCode: string) => {
      this.snackBar.open(
        errorReponse.error.error_description,
        statusCode,
        snackBarConfig
      );
    });
  }
}
